import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import jsPDF from "jspdf"
import "jspdf-autotable"
import { usePermissions } from "API/PermissionContext"
import classnames from "classnames"

const TABS = [
  { key: "1", label: "Producer Cash Advance Report", endpoint: "reports/producer-cash-advance" },
  { key: "2", label: "Producer Loan Report", endpoint: "reports/producer-loan" },
  { key: "3", label: "Producer Material Advance Report", endpoint: "reports/producer-material-advance" },
  { key: "4", label: "Producer Recovery Report", endpoint: "reports/producer-recovery" },
]

const ProducerAdvancesReport = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS
  const [activeTab, setActiveTab] = useState("1")
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [results, setResults] = useState([])
  const [allCenters, setAllCenters] = useState([])
  const [centers, setCenters] = useState([])
  const [routes, setRoutes] = useState([])
  const [allPlants, setAllPlants] = useState([])
  const [plants, setPlants] = useState([])
  const [companies, setCompanies] = useState([])
  const [collectionCenters, setCollectionCenters] = useState([])
  const [producers, setProducers] = useState([])

  useEffect(() => { loadData() }, [])
  useEffect(() => { setSubmitted(false); setResults([]) }, [activeTab])

  const loadData = async () => {
    try {
      const [cResp, pResp, coResp, ccResp, prResp] = await Promise.all([
        api.get("centers"), api.get("plants"), api.get("companies"),
        api.get("collection-centers"), api.get("producers")
      ])
      const chData = Array.isArray(cResp.data.data) ? cResp.data.data : cResp.data.data?.data || []
      setAllCenters(chData)
      setCenters(chData)
      const pData = Array.isArray(pResp.data.data) ? pResp.data.data : pResp.data.data?.data || []
      setAllPlants(pData)
      setPlants(pData)
      setCompanies(Array.isArray(coResp.data.data) ? coResp.data.data : coResp.data.data?.data || [])
      const ccData = Array.isArray(ccResp.data.data) ? ccResp.data.data : ccResp.data.data?.data || []
      setCollectionCenters(ccData)
      const prData = Array.isArray(prResp.data.data) ? prResp.data.data : prResp.data.data?.data || []
      setProducers(prData)
    } catch (e) { console.error(e) }
  }

  const companyChange = async e => {
    const val = e.target.value; setCenters(allCenters); setRoutes([]); setCollectionCenters([])
    if (val && val !== "") {
      try { const r = await api.get("plants-by-company/" + val); setPlants(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){}
    } else setPlants(allPlants)
  }

  const plantChange = async e => {
    const val = e.target.value; setRoutes([]); setCollectionCenters([])
    if (val && val !== "") {
      try { const r = await api.get("chillings-by-plant/" + val); setCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){}
    } else setCenters(allCenters)
  }

  const ccChange = async e => {
    try {
      const val = e.target.value
      const u = !val || val === "0" ? "routes" : "route-cc/" + val
      const r = await api.get(u)
      setRoutes(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
    } catch (e) { console.error(e) }
  }

  const routeChange = async e => {
    try {
      const val = e.target.value
      if (val && val !== "0") {
        const r = await api.get("collection-center-route/" + val)
        const d = Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []
        setCollectionCenters(d)
      } else {
        const r = await api.get("collection-centers")
        setCollectionCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      }
    } catch (e) { console.error(e) }
  }

  const centerChange = async e => {
    try {
      const val = e.target.value
      if (val && val !== "0") {
        const r = await api.get("producer-center/" + val)
        const d = Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []
        setProducers(d)
      } else {
        const r = await api.get("producers")
        setProducers(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
      }
    } catch (e) { console.error(e) }
  }

  const tab = TABS.find(t => t.key === activeTab)

  const handleSubmit = async (e, v) => {
    setLoading(true)
    try {
      const resp = await api.post(tab.endpoint, v)
      if (resp.data.success) { setResults(resp.data.data || []); setSubmitted(true) }
      else { setResults([]); setSubmitted(true) }
    } catch (err) { console.error(err); setResults([]); setSubmitted(true) }
    setLoading(false)
  }

  const downloadPdf = () => {
    const pdf = new jsPDF("l")
    pdf.setFontSize(14)
    pdf.text(tab.label, 14, 15)
    pdf.autoTable({ html: "#producerAdvTbl", startY: 22 })
    pdf.save(tab.label.replace(/ /g, "-") + ".pdf")
  }

  const isMatTab = activeTab === "3"
  const isRecTab = activeTab === "4"
  const isLoanTab = activeTab === "2"

  const getDateLabel = () => {
    if (isLoanTab) return "Loan"
    if (isRecTab) return "Recovery"
    return "Advance"
  }

  const showCompany = companies.length > 1

  const renderForm = () => (
    <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleSubmit(e, v)}>
      {showCompany && <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company" onChange={companyChange}><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
      </Row>}
      <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="plant" label="Plant" onChange={plantChange}><option value="">All</option>{plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="chillingCenter" label="Chilling Center" onChange={ccChange}><option value="">All</option>{centers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="route" label="Route" onChange={routeChange}><option value="">All</option>{routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><AvField type="select" name="center" label="Company Center" onChange={centerChange}><option value="">All</option>{collectionCenters.map((c, i) => <option key={i} value={c.id}>{c.centerName || c.name}</option>)}</AvField></div></Col>
      </Row>
      <Row>
        <Col md={3}><div className="mb-3"><AvField type="select" name="producer" label="Producer"><option value="">All</option><option value="0">All</option>{producers.map((p, i) => <option key={i} value={p.id}>{p.producerName || p.name}</option>)}</AvField></div></Col>
        <Col md={3}><div className="mb-3"><label className="form-label">Status</label><select name="status" className="form-select"><option value="">All</option><option value="pending">Pending</option><option value="closed">Closed</option></select></div></Col>
        <Col md={3}><div className="mb-3"><AvField name="from" label={`${getDateLabel()} Date From`} type="date" /></div></Col>
        <Col md={3}><div className="mb-3"><AvField name="to" label={`${getDateLabel()} Date To`} type="date" /></div></Col>
      </Row>
      {isMatTab && (
        <Row>
          <Col md={4}><div className="mb-3"><AvRadioGroup inline name="showDeductionItemwise" label="Show Deduction Itemwise" value="no"><AvRadio label="Yes" value="yes" /><AvRadio label="No" value="no" /></AvRadioGroup></div></Col>
        </Row>
      )}
      <div className="mb-3 mt-1"><button className="btn btn-primary w-md" type="submit" disabled={loading}>{loading ? <><Spinner size="sm" /> Submitting...</> : <><i className="fas fa-check me-1"></i>Submit</>}</button></div>
    </AvForm>
  )

  const renderCashTable = () => (
    <Table id="producerAdvTbl" className="dairyTbl" size="sm" bordered>
      <thead><tr>
        <th>S.No</th><th>Date</th><th>Producer</th><th>Chilling Center</th><th>Route</th><th>Company Center</th><th>Advance Amount</th><th>Deduction Amount</th><th>Balance</th><th>Status</th><th>Remarks</th>
      </tr></thead>
      <tbody>
        {results.length > 0 ? results.map((r, i) => (
          <tr key={i}>
            <td>{i + 1}</td><td>{r.advanceDate}</td><td>{r.producerName}</td><td>{r.ccName}</td><td>{r.routeName}</td><td>{r.centerName}</td>
            <td className="text-end">{parseFloat(r.advanceAmount).toFixed(2)}</td>
            <td className="text-end">{parseFloat(r.deductionAmount).toFixed(2)}</td>
            <td className="text-end">{parseFloat(r.balance).toFixed(2)}</td>
            <td><span className={`badge bg-${r.status === 'Closed' ? 'success' : 'warning'}`}>{r.status}</span></td>
            <td>{r.remarks}</td>
          </tr>
        )) : <tr><td colSpan="11" className="text-center py-3">No Results Found</td></tr>}
      </tbody>
      {results.length > 0 && <tfoot><tr style={{ fontWeight: 700, background: "#f1f5f9" }}>
        <td colSpan="6">TOTAL</td>
        <td className="text-end">{results.reduce((s, r) => s + parseFloat(r.advanceAmount || 0), 0).toFixed(2)}</td>
        <td className="text-end">{results.reduce((s, r) => s + parseFloat(r.deductionAmount || 0), 0).toFixed(2)}</td>
        <td className="text-end">{results.reduce((s, r) => s + parseFloat(r.balance || 0), 0).toFixed(2)}</td>
        <td colSpan="2"></td>
      </tr></tfoot>}
    </Table>
  )

  const renderLoanTable = () => (
    <Table id="producerAdvTbl" className="dairyTbl" size="sm" bordered>
      <thead><tr>
        <th>S.No</th><th>Date</th><th>Producer</th><th>Chilling Center</th><th>Route</th><th>Company Center</th><th>Loan Amount</th><th>Deduction Amount</th><th>Balance</th><th>Status</th><th>Remarks</th>
      </tr></thead>
      <tbody>
        {results.length > 0 ? results.map((r, i) => (
          <tr key={i}>
            <td>{i + 1}</td><td>{r.loanDate}</td><td>{r.producerName}</td><td>{r.ccName}</td><td>{r.routeName}</td><td>{r.centerName}</td>
            <td className="text-end">{parseFloat(r.loanAmount).toFixed(2)}</td>
            <td className="text-end">{parseFloat(r.deductionAmount).toFixed(2)}</td>
            <td className="text-end">{parseFloat(r.balance).toFixed(2)}</td>
            <td><span className={`badge bg-${r.status === 'Closed' ? 'success' : 'warning'}`}>{r.status}</span></td>
            <td>{r.remarks}</td>
          </tr>
        )) : <tr><td colSpan="11" className="text-center py-3">No Results Found</td></tr>}
      </tbody>
      {results.length > 0 && <tfoot><tr style={{ fontWeight: 700, background: "#f1f5f9" }}>
        <td colSpan="6">TOTAL</td>
        <td className="text-end">{results.reduce((s, r) => s + parseFloat(r.loanAmount || 0), 0).toFixed(2)}</td>
        <td className="text-end">{results.reduce((s, r) => s + parseFloat(r.deductionAmount || 0), 0).toFixed(2)}</td>
        <td className="text-end">{results.reduce((s, r) => s + parseFloat(r.balance || 0), 0).toFixed(2)}</td>
        <td colSpan="2"></td>
      </tr></tfoot>}
    </Table>
  )

  const renderMaterialTable = () => (
    <Table id="producerAdvTbl" className="dairyTbl" size="sm" bordered>
      <thead><tr>
        <th>S.No</th><th>Date</th><th>Producer</th><th>Chilling Center</th><th>Route</th><th>Company Center</th><th>Category</th><th>Item</th><th>Qty</th><th>Rate</th><th>Total Amount</th><th>Deduction</th><th>Balance</th><th>Status</th><th>Remarks</th>
      </tr></thead>
      <tbody>
        {results.length > 0 ? results.map((r, i) => (
          <tr key={i}>
            <td>{i + 1}</td><td>{r.advanceDate}</td><td>{r.producerName}</td><td>{r.ccName}</td><td>{r.routeName}</td><td>{r.centerName}</td>
            <td>{r.materialCategory}</td><td>{r.materialItem}</td><td>{r.quantity}</td>
            <td className="text-end">{parseFloat(r.rate).toFixed(2)}</td>
            <td className="text-end">{parseFloat(r.totalAmount).toFixed(2)}</td>
            <td className="text-end">{parseFloat(r.deductionAmount).toFixed(2)}</td>
            <td className="text-end">{parseFloat(r.balance).toFixed(2)}</td>
            <td><span className={`badge bg-${r.status === 'Closed' ? 'success' : 'warning'}`}>{r.status}</span></td>
            <td>{r.remarks}</td>
          </tr>
        )) : <tr><td colSpan="15" className="text-center py-3">No Results Found</td></tr>}
      </tbody>
      {results.length > 0 && <tfoot><tr style={{ fontWeight: 700, background: "#f1f5f9" }}>
        <td colSpan="10">TOTAL</td>
        <td className="text-end">{results.reduce((s, r) => s + parseFloat(r.totalAmount || 0), 0).toFixed(2)}</td>
        <td className="text-end">{results.reduce((s, r) => s + parseFloat(r.deductionAmount || 0), 0).toFixed(2)}</td>
        <td className="text-end">{results.reduce((s, r) => s + parseFloat(r.balance || 0), 0).toFixed(2)}</td>
        <td colSpan="2"></td>
      </tr></tfoot>}
    </Table>
  )

  const renderRecoveryTable = () => (
    <Table id="producerAdvTbl" className="dairyTbl" size="sm" bordered>
      <thead><tr>
        <th>S.No</th><th>Date</th><th>Producer</th><th>Chilling Center</th><th>Route</th><th>Company Center</th><th>Recovery Amount</th><th>Remarks</th>
      </tr></thead>
      <tbody>
        {results.length > 0 ? results.map((r, i) => (
          <tr key={i}>
            <td>{i + 1}</td><td>{r.recoveryDate}</td><td>{r.producerName}</td><td>{r.ccName}</td><td>{r.routeName}</td><td>{r.centerName}</td>
            <td className="text-end">{parseFloat(r.recoveryAmount).toFixed(2)}</td>
            <td>{r.remarks}</td>
          </tr>
        )) : <tr><td colSpan="8" className="text-center py-3">No Results Found</td></tr>}
      </tbody>
      {results.length > 0 && <tfoot><tr style={{ fontWeight: 700, background: "#f1f5f9" }}>
        <td colSpan="6">TOTAL</td>
        <td className="text-end">{results.reduce((s, r) => s + parseFloat(r.recoveryAmount || 0), 0).toFixed(2)}</td>
        <td></td>
      </tr></tfoot>}
    </Table>
  )

  const renderTable = () => {
    switch (activeTab) {
      case "1": return renderCashTable()
      case "2": return renderLoanTable()
      case "3": return renderMaterialTable()
      case "4": return renderRecoveryTable()
      default: return null
    }
  }

  const currentTabLabel = TABS.find(t => t.key === activeTab)?.label || ""

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader notToPrint"><Row><Col md={12}><h4>{currentTabLabel}</h4></Col></Row></div>
      <div className="dashContent">
        <Nav tabs className="nav-tabs-custom mb-3 notToPrint">
          {TABS.map(t => (
            <NavItem key={t.key}>
              <NavLink className={classnames({ active: activeTab === t.key })} onClick={() => setActiveTab(t.key)} style={{ cursor: "pointer" }}>{t.label}</NavLink>
            </NavItem>
          ))}
        </Nav>
        <TabContent activeTab={activeTab}>
          {TABS.map(t => (
            <TabPane key={t.key} tabId={t.key}>
              {activeTab === t.key && <>
                {renderForm()}
                {submitted && <>
                  {canExport(MID) && <div className="exportBtns notToPrint mb-2">
                    <button className="btn btn-info btn-sm m-1" onClick={downloadPdf}><i className="fas fa-file-pdf me-1"></i>PDF</button>
                    <button className="btn btn-info btn-sm m-1" onClick={() => window.print()}><i className="fas fa-print me-1"></i>Print</button>
                  </div>}
                  <div className="table-responsive">{renderTable()}</div>
                </>}
              </>}
            </TabPane>
          ))}
        </TabContent>
      </div>
    </div>
  )
}
export default ProducerAdvancesReport
